/**
 * Design tokens for the whole app.
 *
 * The look is meant to read like a newspaper puzzle page: warm paper, black
 * ink, hairline rules, and exactly one accent colour. Flat fills only — no
 * gradients anywhere. Import these instead of hardcoding hex values so the
 * palette stays in one place.
 */

export const colors = {
  // Surfaces
  paper: '#f4f0e7',      // page background
  card: '#fffdf9',       // raised surfaces (tables, panels, modal)
  cardMuted: '#efe9dc',  // inset / secondary surfaces

  // Ink
  ink: '#1c1b17',        // primary text, grid borders, filled letters
  inkSoft: '#57524a',    // secondary text
  inkFaint: '#8d8578',   // labels, timestamps, placeholder

  // Lines
  rule: '#ddd5c6',       // hairline borders
  ruleStrong: '#c3b9a6', // emphasised borders

  // One accent, used sparingly: links, primary buttons, active states.
  accent: '#b03a25',
  accentHover: '#8f2d1c',
  accentWash: '#f7e7e2',

  // Grid-specific. Yellow-on-blue is the convention solvers already know.
  cellSelected: '#f5cf47',
  cellSelectedEdge: '#d9a90f',
  cellWord: '#d5e3f0',
  cellBlocked: '#1c1b17',

  // Feedback
  success: '#3d7a51',
  successWash: '#e6efe6',
  danger: '#a33325',
  dangerWash: '#f7e7e2',

  // Medals for the top three leaderboard rows
  gold: '#c99700',
  silver: '#8a8a8a',
  bronze: '#a06a3c',
} as const;

export const fonts = {
  /** Headlines and puzzle titles. A serif immediately reads as "print". */
  display: "Georgia, 'Iowan Old Style', 'Palatino Linotype', 'Times New Roman', serif",
  /** UI text. */
  body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  /** Timers and times — always with tabular figures so digits don't jitter. */
  mono: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
} as const;

export const radius = {
  sm: '3px',
  md: '5px',
  lg: '8px',
} as const;

export const shadow = {
  /** Barely-there lift for panels sitting on paper. */
  panel: '0 1px 2px rgba(28, 27, 23, 0.06)',
  /** The grid and modals get a slightly firmer shadow. */
  raised: '0 2px 10px rgba(28, 27, 23, 0.12)',
  modal: '0 12px 40px rgba(28, 27, 23, 0.24)',
} as const;

/** Small-caps section label used above tables throughout the app. */
export const sectionLabel = `
  font-family: ${fonts.body};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${colors.inkFaint};
`;

/** Times, counts, and the timer. Tabular figures keep columns aligned. */
export const numeric = `
  font-family: ${fonts.mono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

/** Primary action button. Flat fill, 1px border, no lift-on-hover. */
export const buttonPrimary = `
  font-family: ${fonts.body};
  font-size: 14px;
  font-weight: 600;
  padding: 9px 18px;
  border-radius: ${radius.sm};
  border: 1px solid ${colors.accent};
  background: ${colors.accent};
  color: ${colors.card};
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;

  &:hover:not(:disabled) {
    background: ${colors.accentHover};
    border-color: ${colors.accentHover};
  }

  &:disabled {
    background: ${colors.cardMuted};
    border-color: ${colors.rule};
    color: ${colors.inkFaint};
    cursor: not-allowed;
  }
`;

/** Secondary action button — outlined, sits quietly next to the primary. */
export const buttonSecondary = `
  font-family: ${fonts.body};
  font-size: 14px;
  font-weight: 600;
  padding: 9px 18px;
  border-radius: ${radius.sm};
  border: 1px solid ${colors.ruleStrong};
  background: ${colors.card};
  color: ${colors.ink};
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;

  &:hover:not(:disabled) {
    background: ${colors.cardMuted};
    border-color: ${colors.inkFaint};
  }

  &:disabled {
    background: ${colors.cardMuted};
    border-color: ${colors.rule};
    color: ${colors.inkFaint};
    cursor: not-allowed;
  }
`;

/** Visible keyboard focus for anything interactive. */
export const focusRing = `
  &:focus-visible {
    outline: 2px solid ${colors.accent};
    outline-offset: 2px;
  }
`;
